import apiClient from "./api-client";
import { handleApiError } from "./error-handler";
import {
	FreelancerExpertise,
	Tool as ClientTool,
	CommunicationChannel,
	FreelancerFrequency
} from "../models/Client";
import { FreelancingYearsOfExperience } from "../models/FreelancingYearsOfExperience";
import { ExpertiseExperience } from "../models/ExpertiseExperience";
import { DataProcessStage } from "../models/DataProcessStage";
import { Language } from "../models/Language";
import { LanguageProficiency } from "../models/LanguageProficiency";
import { Region } from "../models/Region";
import { Sector } from "../models/Sector";
import { BusinessDomain } from "../models/BusinessDomain";
import { JobType } from "../models/JobType";
import { JobLocation } from "../models/JobLocation";
import { Skill } from "../models/Skill";
import { DocumentType } from "../models/DocumentType";
import { UrlType } from "../models/UrlType";
import { MissionsDuration } from "../models/MissionsDuration";
import { Expertise } from "../models/Expertise";
import { Experience } from "../models/Experience";
import { Tool } from "../models/Tool";
import { ApplicationNoteSubject } from "../models/ApplicationNoteSubject";

/**
 * Get freelancer expertise options
 */
export const getFreelancerExpertises = async (): Promise<FreelancerExpertise[]> => {
	try {
		const response = await apiClient.get("/freelancer-expertises");
		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get freelancer expertise options");
	}
};

/**
 * Get tools for client onboarding
 */
export const getClientOnboardingTools = async (): Promise<ClientTool[]> => {
	try {
		const response = await apiClient.get("/tools?isClientOnboarding=true");
		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get tool options");
	}
};

/**
 * Get business domain options
 */
export const getBusinessDomains = async (): Promise<BusinessDomain[]> => {
	try {
		const response = await apiClient.get("/business-domains");
		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get business domain options");
	}
};

/**
 * Get communication channel options
 */
export const getCommunicationChannels = async (): Promise<CommunicationChannel[]> => {
	try {
		const response = await apiClient.get("/communication-channels");
		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get communication channel options");
	}
};

/**
 * Get freelancer frequency options
 */
export const getFreelancerFrequencies = async (): Promise<FreelancerFrequency[]> => {
	try {
		const response = await apiClient.get("/freelancer-frequencies");
		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get freelancer frequency options");
	}
};

export const GetExpertiseExperiencesAsync = async (): Promise<
	ReadonlyArray<ExpertiseExperience>
> => {
	try {
		const response = await apiClient.get("/expertise-experiences");

		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get expertise and experience options");
	}
};

export const GetFreelancingYearsOfExperiencesAsync = async (): Promise<
	ReadonlyArray<FreelancingYearsOfExperience>
> => {
	try {
		const response = await apiClient.get("/freelancing-years-of-experiences");

		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get freelancing years of experiences options");
	}
};

export const GetDataProcessStagesByExpertiseExperienceIdAsync = async (
	expertiseExperienceId: number | undefined
): Promise<ReadonlyArray<DataProcessStage>> => {
	try {
		if (!expertiseExperienceId) {
			return [];
		}
		const response = await apiClient.get(
			`/data-process-stages?expertiseExperienceId=${expertiseExperienceId}`
		);

		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get data process stages options");
	}
};

export const GetAllLanguagesAsync = async (): Promise<ReadonlyArray<Language>> => {
	try {
		const response = await apiClient.get("languages");

		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get languages options");
	}
};

export const GetAllLanguageProficienciesAsync = async (): Promise<
	ReadonlyArray<LanguageProficiency>
> => {
	try {
		const response = await apiClient.get("language-proficiencies");

		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get language proficiencies options");
	}
};

export const GetAllRegionsAsync = async (): Promise<ReadonlyArray<Region>> => {
	try {
		const response = await apiClient.get("regions");

		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get regions options");
	}
};

export const GetAllSectorsAsync = async (): Promise<ReadonlyArray<Sector>> => {
	try {
		const response = await apiClient.get("sectors");

		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get sectors options");
	}
};

export const GetAllBusinessDomainsAsync = async (): Promise<
	ReadonlyArray<BusinessDomain>
> => {
	try {
		const response = await apiClient.get("business-domains");

		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get business domains options");
	}
};

export const GetAllJobTypesAsync = async (): Promise<ReadonlyArray<JobType>> => {
	try {
		const response = await apiClient.get("job-types");

		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get job types options");
	}
};

export const GetAllJobLocationsAsync = async (): Promise<ReadonlyArray<JobLocation>> => {
	try {
		const response = await apiClient.get("job-locations");

		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get job locations options");
	}
};

export const GetAllSkillsAsync = async (): Promise<ReadonlyArray<Skill>> => {
	try {
		const response = await apiClient.get("skills");

		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get skills options");
	}
};

export const GetAllMissionsDurationsAsync = async (): Promise<
	ReadonlyArray<MissionsDuration>
> => {
	try {
		const response = await apiClient.get("missions-durations");

		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get missions durations options");
	}
};

export const GetAllDocumentTypesAsync = async (): Promise<
	ReadonlyArray<DocumentType>
> => {
	try {
		const response = await apiClient.get("document-types");

		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get document types options");
	}
};

export const GetAllUrlTypesAsync = async (): Promise<ReadonlyArray<UrlType>> => {
	try {
		const response = await apiClient.get("url-types");

		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get url types options");
	}
};

export const GetAllExpertisesAsync = async (): Promise<ReadonlyArray<Expertise>> => {
	try {
		const response = await apiClient.get("/expertises");

		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get expertise options");
	}
};

export const GetAllExperiencesAsync = async (): Promise<ReadonlyArray<Experience>> => {
	try {
		const response = await apiClient.get("/experiences");

		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get experience options");
	}
};

export const GetAllToolsAsync = async (): Promise<ReadonlyArray<Tool>> => {
	try {
		const response = await apiClient.get("/tools");

		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get tools options");
	}
};

export const GetAllApplicationNoteSubjectsAsync = async (): Promise<
	ReadonlyArray<ApplicationNoteSubject>
> => {
	try {
		const response = await apiClient.get("/application-note-subjects");

		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to get application note subjects options");
	}
};
